import { useCallback, useEffect, useMemo, useRef } from "react";

import { TEXTAREA_CONFIG } from "./AutoExpandingTextarea.config";
import { calculateHeights } from "./AutoExpandingTextarea.utils";

export interface UseAutoExpandingHeightOptions {
  maxLines?: number;
  onHeightChange?: (height: number) => void;
  value?: string;
}

export interface UseAutoExpandingHeightReturn {
  adjustHeight: () => void;
  maxHeight: number;
  minHeight: number;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

/**
 * Custom hook for managing auto-expanding textarea height
 * @param options - Configuration options for the hook
 * @returns Object with ref, heights, and adjustment function
 */
export const useAutoExpandingHeight = ({
  maxLines = TEXTAREA_CONFIG.maxLines,
  onHeightChange,
  value,
}: UseAutoExpandingHeightOptions = {}): UseAutoExpandingHeightReturn => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Calculate heights using configuration
  const { maxHeight, minHeight } = useMemo(
    () => calculateHeights(maxLines, TEXTAREA_CONFIG),
    [maxLines],
  );

  // Memoized height adjustment function with error handling
  const adjustHeight = useCallback((): void => {
    try {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate the new height based on content
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

      // Set the new height
      textarea.style.height = `${newHeight}px`;

      // Enable/disable scrolling based on whether content exceeds max height
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.overflowY = "hidden";
      }

      // Notify parent component of height change if callback provided
      if (onHeightChange) {
        onHeightChange(newHeight);
      }
    } catch (error) {
      console.warn("Failed to adjust textarea height:", error);
    }
  }, [minHeight, maxHeight, onHeightChange]);

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Adjust height on mount
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  return {
    adjustHeight,
    maxHeight,
    minHeight,
    textareaRef,
  };
};
